import { clientLogging, cookieService } from '@bfa/nextjs-common/services';
import { MessageLevel } from '@bfa/nextjs-common/services/loggingService/LoggingServiceInterface';

import { SUBSCRIPTION_FLOWS } from '../../../constants';
import {
  GQLDExperiment,
  GQLGbxUpgradeMutation,
  GQLProgram,
  GQLUserSubscription,
  Maybe,
} from '../../../gql.generated';
import { updateCookie } from '../../../utils/cookies';
import CookieName, {
  TTL_24_HOURS_IN_SECONDS,
} from '../../../utils/cookies/cookieName';
import { getGqlProgram } from '../../../utils/getGqlProgram';
import { DEFAULT_ERROR_MESSAGE } from '../constants';

export const getExperiment = (version?: string): GQLDExperiment | undefined => {
  if (!version) return undefined;

  const experimentMap = new Map([
    ['v1', GQLDExperiment.GBX_STANDARD_44],
    ['v2', GQLDExperiment.GBX_STANDARD_35],
  ]);

  return experimentMap.get(version);
};

const getBaseProgramFromSubs = (subs: GQLUserSubscription[]) => {
  // FULLSIZE is checked first as it has higher priority to be a base program
  if (subs.some((sub) => sub.program === GQLProgram.FULLSIZE))
    return GQLProgram.FULLSIZE;
  if (subs.some((sub) => sub.program === GQLProgram.GLAMBAG))
    return GQLProgram.GLAMBAG;
  return null;
};

export const getBaseProgram = (subs: GQLUserSubscription[], program?: string) =>
  getBaseProgramFromSubs(subs) || getGqlProgram(program as string);

export const getCurrentFlow = (
  fromCheckout: boolean,
  flow?: string | string[],
  upsaleFlow?: string
) => {
  if (flow) {
    clientLogging.logInfo({
      content: `[GWS Eligibility] Flow: ${flow}`,
      level: MessageLevel.INFO,
    });
    return SUBSCRIPTION_FLOWS.UES;
  }
  if (upsaleFlow) {
    return upsaleFlow as SUBSCRIPTION_FLOWS;
  }
  if (!fromCheckout) {
    return SUBSCRIPTION_FLOWS.ADD;
  }
  return undefined;
};

type ExtractGbxUpgradeDataProps = {
  gbxUpgrade?: Maybe<GQLGbxUpgradeMutation>;
};

export const extractGbxUpgradeData = (
  data?: Maybe<ExtractGbxUpgradeDataProps>
) => ({
  isPaymentDecline:
    data?.gbxUpgrade?.gbxUpgradeTransaction?.isPaymentDecline ?? false,
  isGiftIncluded: data?.gbxUpgrade?.gbxUpgradeTransaction?.isWithGift ?? false,
  errorTryingToUpgrade: data?.gbxUpgrade?.gbxUpgradeTransaction?.error ?? true,
  errorMessage:
    data?.gbxUpgrade?.gbxUpgradeTransaction?.errorMessage ??
    DEFAULT_ERROR_MESSAGE,
});

export const getSubConfirmationSuccessCookie = (fromCheckout: boolean) =>
  fromCheckout
    ? `${cookieService.getCookie(
        null,
        CookieName.SUB_CONFIRMATION_SUCCESS_PROGRAM
      )},${GQLProgram.GBX}`
    : GQLProgram.GBX;

export const getCurrentFlowForAnalytics = (
  flow?: string | string[],
  upsaleFlow?: string
) => {
  const cookie = cookieService.getCookie(
    null,
    CookieName.SUB_CONFIRMATION_FLOW
  );
  if (!flow && !upsaleFlow) {
    clientLogging.logInfo({
      content: `[GWS Eligibility] Cookie: ${cookie}`,
      level: MessageLevel.INFO,
    });
  }
  return flow || upsaleFlow || cookie;
};

export const updateCookieOnAddonsOrUP = (
  currentFlow?: SUBSCRIPTION_FLOWS,
  program?: GQLProgram
) => {
  if (
    currentFlow === SUBSCRIPTION_FLOWS.ADDONS ||
    currentFlow === SUBSCRIPTION_FLOWS.UP
  ) {
    // Updates the cookie only when the user is coming from Addons or UP, so can be redirected to the thank you page
    updateCookie(true, null, CookieName.SUB_CONFIRMATION_PROGRAM, program, {
      maxAge: TTL_24_HOURS_IN_SECONDS,
    });
  }
};

export const shouldRedirectOnPaymentDecline = (
  paymentRetryEnabled: boolean,
  isPaymentDecline: boolean
) => paymentRetryEnabled && isPaymentDecline;
